/**
 * Saldar Tricount (puro): qué se apunta en tu libro para que diga lo que de verdad te ha costado la vida en común.
 * Lo de Tricount siempre es compartido y lo que te paga la otra persona al saldar no es un ingreso:
 * - Tus gastos pagados desde el banco (en tu libro, compartidos): ya cuenta tu mitad → nada (los no marcados como compartidos se marcan).
 * - Tus gastos en efectivo (no están en tu libro: el efectivo se da por gastado al sacarlo, sin categoría) → + la mitad, sin categoría.
 * - Lo que pagó la otra persona → − tu mitad, en su categoría y en su mes (agrupado por categoría y mes).
 * - La transferencia del saldo → categoría «Saldo Tricount» (no cuenta).
 * Todo lo apuntado va a la cuenta técnica «Tricount» (fuera del patrimonio), con lo que te deben de tus gastos y el propio saldo:
 * tras saldar queda a 0 € (es la comprobación de que cuadra).
 */
import { categorize, monthEnd, round2, type Movement } from '@pf/domain';
import { newId, type Command, type MetaRow } from '@pf/workbook';
import type { Computed } from './model.js';
import { accountUse } from './account-use.js';
import { today } from './clock.js';
import type { TricountItem, TricountSummary } from './tricount-model.js';

export const TRICOUNT_ACCOUNT = 'Tricount';
export const SETTLE_CATEGORY = 'Saldo Tricount';
const MONTHS = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
const monthWord = (m: string) => `${MONTHS[Number(m.slice(5, 7)) - 1]}${m.slice(0, 4) === today().slice(0, 4) ? '' : ` ${m.slice(0, 4)}`}`;

export interface SettleGroup { month: string; category: string; amount: number; items: TricountItem[] }
export interface SettlePlan {
  me: string; other: string;
  /** > 0: el otro te paga; < 0: pagas tú. */
  balance: number; payer: string; receiver: string; amount: number;
  since: string; until: string; count: number; total: number;
  /** Tu mitad de lo que pagó el otro (por categoría y mes). */
  fromOther: SettleGroup[];
  /** La mitad de tus gastos en efectivo, que te devuelve (por mes). */
  myCash: SettleGroup[];
  /** Tus gastos que ya están en tu libro, y los que hay que marcar como compartidos. */
  inBook: Array<{ item: TricountItem; movementId: string }>;
  toShare: Array<{ item: TricountItem; movementId: string }>;
  /** Tuyos pagados con cuenta que no encuentro en tu libro (se toman como ya apuntados). */
  notFound: TricountItem[];
}

/** Tu gasto en tu libro para una fila tuya: el enviado desde la app o el de misma fecha (±3 días) e importe. */
function bookMovement(c: Computed, x: TricountItem, used: Set<string>) {
  if (x.movementId) return c.movements.find(m => m.id === x.movementId);
  const t = Date.parse(x.date);
  const cands = c.movements.filter(m => !used.has(m.id) && m.amount < 0 && m.account !== TRICOUNT_ACCOUNT && Math.abs(Math.abs(m.amount) - x.amount) < 0.005
    && Math.abs(Date.parse(m.date) - t) <= 3 * 86400000);
  return cands.find(m => m.effShared) ?? cands[0];
}

export function settlePlan(c: Computed, sum: TricountSummary): SettlePlan {
  const used = new Set<string>();
  const inBook: SettlePlan['inBook'] = [], toShare: SettlePlan['toShare'] = [], notFound: TricountItem[] = [];
  const cash = new Map<string, SettleGroup>(), other = new Map<string, SettleGroup>();
  const add = (map: Map<string, SettleGroup>, month: string, category: string, x: TricountItem) => {
    const k = `${month}|${category}`;
    const g = map.get(k) ?? map.set(k, { month, category, amount: 0, items: [] }).get(k)!;
    g.amount = round2(g.amount + x.amount / 2);
    g.items.push(x);
  };
  for (const x of sum.items) {
    const month = x.date.slice(0, 7);
    if (!x.mine) {
      const cat = categorize(c.wb.rules, c.wb.categories, '', x.concept, -x.amount);
      add(other, month, cat.isDefault ? '' : cat.category, x);
      continue;
    }
    const m = bookMovement(c, x, used);
    if (m) { used.add(m.id); (m.effShared ? inBook : toShare).push({ item: x, movementId: m.id }); continue; }
    if (/efectivo/i.test(x.origin)) add(cash, month, '', x);
    else notFound.push(x);
  }
  const dates = sum.items.map(x => x.date).sort();
  const sort = (g: SettleGroup[]) => g.sort((a, b) => a.month.localeCompare(b.month) || b.amount - a.amount);
  return {
    me: sum.me, other: sum.other, balance: sum.balance,
    payer: sum.balance > 0 ? sum.other : sum.me, receiver: sum.balance > 0 ? sum.me : sum.other, amount: round2(Math.abs(sum.balance)),
    since: dates[0] ?? '', until: dates[dates.length - 1] ?? '', count: sum.items.length, total: round2(sum.items.reduce((t, x) => t + x.amount, 0)),
    fromOther: sort([...other.values()]), myCash: sort([...cash.values()]), inBook, toShare, notFound,
  };
}

/** Lo que queda en la cuenta técnica tras apuntarlo todo (debe ser 0: si no, algo no cuadra). */
export function settleCheck(p: SettlePlan): number {
  const owed = [...p.inBook, ...p.toShare].reduce((t, x) => t + x.item.amount / 2, 0) + p.notFound.reduce((t, x) => t + x.amount / 2, 0);
  return round2(owed + p.myCash.reduce((t, g) => t + g.amount, 0) - p.fromOther.reduce((t, g) => t + g.amount, 0) - p.balance);
}

/**
 * Los comandos de «Saldar», en orden: categoría y cuenta técnica (si faltan), el saldo en el libro de Tricount,
 * los movimientos de la cuenta técnica, y en tu libro la transferencia (a «Saldo Tricount») y los que se marcan compartidos.
 * categories: categoría elegida para cada grupo de lo que pagó el otro (clave «mes|categoría propuesta»).
 */
/** Clave de un gasto de Tricount (para elegirle categoría uno a uno). */
export const itemKey = (x: Pick<TricountItem, 'row' | 'date' | 'amount'>) => `${x.row}|${x.date}|${x.amount}`;

export function settleCommands(c: Computed, p: SettlePlan, o: { date: string; transferId: string; categories?: Record<string, string>;
  /** Categoría elegida para un gasto concreto (gana a la de su grupo). */
  itemCategories?: Record<string, string>;
  /** Reglas nuevas («si el concepto contiene X → categoría»), para que la próxima vez se clasifiquen solos. */
  rules?: Array<{ keyword: string; category: string }> }): { commands: Command[]; opId: string } {
  const opId = newId('S');
  const cmds: Command[] = [];
  const is = (a: string, b: string) => a.toLowerCase() === b.toLowerCase();
  if (!c.wb.categories.some(x => is(x.name, SETTLE_CATEGORY)))
    cmds.push({ type: 'add-category', category: { name: SETTLE_CATEGORY, kind: 'excluded', nature: '', keywords: [] } });
  if (!c.wb.accounts.some(a => is(a.name, TRICOUNT_ACCOUNT)))
    cmds.push({ type: 'add-account', account: { name: TRICOUNT_ACCOUNT, bank: '', type: 'Corriente', share: 1, inNetWorth: false, opening: 0, openingDate: p.since || o.date, use: 'Disponible' } });
  cmds.push({ type: 'settle-tricount', settlement: { id: opId, date: o.date, payer: p.payer, receiver: p.receiver, amount: p.amount, movementId: o.transferId,
    note: `${p.count} gastos · tu parte de lo que pagó ${p.other}: ${p.fromOther.reduce((t, g) => t + g.amount, 0).toFixed(2)} €` } });
  const movements: Movement[] = [];
  const at = (month: string) => (monthEnd(month) < o.date ? monthEnd(month) : o.date);
  const mv = (date: string, concept: string, amount: number, category: string, note = ''): Movement =>
    ({ id: newId('T'), date, concept, amount: round2(amount), account: TRICOUNT_ACCOUNT, category, shared: false, group: '', note: note || `Saldo Tricount del ${o.date}`, month: '' });
  /** Los gastos que lleva dentro un apunte sin categoría (para poder dividirlo después): «Moulin rouge 10 € · …». */
  const inside = (items: TricountItem[]) => {
    const parts = items.map(x => `${x.concept.trim() || 'sin concepto'} ${(x.amount / 2).toFixed(2).replace('.', ',')} €`);
    let out = '';
    for (let i = 0; i < parts.length; i++) {
      const next = out ? `${out} · ${parts[i]}` : parts[i]!;
      if (next.length > 170) return `${out} · y ${parts.length - i} más (Histórico de Tricount)`;
      out = next;
    }
    return out;
  };
  // Cada gasto con su categoría (la suya, la de su grupo o la propuesta) y agrupados otra vez por categoría y mes
  const booked = new Map<string, { month: string; category: string; amount: number; items: TricountItem[] }>();
  for (const g of p.fromOther) for (const x of g.items) {
    const category = o.itemCategories?.[itemKey(x)] ?? o.categories?.[`${g.month}|${g.category}`] ?? g.category;
    const k = `${g.month}|${category}`;
    const b = booked.get(k) ?? booked.set(k, { month: g.month, category, amount: 0, items: [] }).get(k)!;
    b.amount += x.amount / 2;
    b.items.push(x);
  }
  for (const b of [...booked.values()].sort((a, c2) => a.month.localeCompare(c2.month) || c2.amount - a.amount))
    movements.push(mv(at(b.month), `Tricount · lo que pagó ${p.other} · ${b.category || 'Sin categoría'} (${monthWord(b.month)})`, -b.amount, b.category,
      b.category ? '' : `Tu mitad de: ${inside(b.items)}`));
  for (const g of p.myCash) movements.push(mv(at(g.month), `Tricount · ${p.other} te devuelve la mitad de tu efectivo (${monthWord(g.month)})`, g.amount, ''));
  // Lo que te debía de tus gastos compartidos (no cuenta: tu mitad ya estaba en tu libro), por mes, y el saldo que lo cierra
  const owed = new Map<string, number>();
  for (const x of [...p.inBook.map(y => y.item), ...p.toShare.map(y => y.item), ...p.notFound]) owed.set(x.date.slice(0, 7), (owed.get(x.date.slice(0, 7)) ?? 0) + x.amount / 2);
  for (const [month, amount] of [...owed.entries()].sort()) movements.push(mv(at(month), `Tricount · mitad de ${p.other} en tus gastos compartidos (${monthWord(month)})`, amount, SETTLE_CATEGORY));
  // El saldo cierra la cuenta técnica: exactamente lo apuntado (los céntimos de redondear mitades no dejan restos)
  movements.push(mv(o.date, p.balance > 0 ? `Tricount · saldo: ${p.other} te paga` : `Tricount · saldo: pagas a ${p.other}`, -movements.reduce((t, m) => t + m.amount, 0), SETTLE_CATEGORY));
  const meta: MetaRow[] = movements.map(m => ({ id: m.id, source: 'tricount', sourceRef: opId, valueDate: '', balanceAfter: null, tricountSentAt: '', tricountAmount: null, importedAt: o.date }));
  cmds.push({ type: 'import-batch', label: `Saldo Tricount ${o.date}`, movements, meta });
  const known = new Set(c.wb.rules.map(r => r.contains.toUpperCase()));
  for (const r of o.rules ?? []) {
    const contains = r.keyword.trim().toUpperCase();
    if (contains.length < 3 || !r.category || known.has(contains)) continue;
    known.add(contains);
    cmds.push({ type: 'add-rule', rule: { priority: 100, account: '', contains, regex: '', amountEquals: null, direction: 'expense', category: r.category, active: true } });
  }
  const raw = (id: string) => c.wb.movements.find(m => m.id === id);
  const t = o.transferId ? raw(o.transferId) : undefined;
  if (t) cmds.push({ type: 'update-movement', id: t.id, row: t.row, fingerprint: t.fingerprint, changes: { category: SETTLE_CATEGORY } });
  for (const x of p.toShare) { const m = raw(x.movementId); if (m) cmds.push({ type: 'update-movement', id: m.id, row: m.row, fingerprint: m.fingerprint, changes: { shared: true } }); }
  return { commands: cmds, opId };
}

/** Transferencias que pueden ser la del saldo: de los últimos 45 días, del signo que toca y por un importe parecido (±1 €). */
export function transferCandidates(c: Computed, p: Pick<SettlePlan, 'balance' | 'amount' | 'other'>, today: string) {
  const from = new Date(Date.parse(today) - 45 * 86400000).toISOString().slice(0, 10);
  const name = p.other.toLowerCase().slice(0, 4);
  return c.movements.filter(m => m.account !== TRICOUNT_ACCOUNT && m.date >= from && m.date <= today && Math.sign(m.amount) === Math.sign(p.balance)
    && Math.abs(Math.abs(m.amount) - p.amount) <= 1 && m.effCategory.toLowerCase() !== SETTLE_CATEGORY.toLowerCase())
    .sort((a, b) => Number(b.concept.toLowerCase().includes(name)) - Number(a.concept.toLowerCase().includes(name)) || b.date.localeCompare(a.date));
}

/** Ingresos (o pagos) de tu libro que parecen saldos de antes con la otra persona: su nombre en el concepto y 50 € o más. */
export function pastSettlementCandidates(c: Computed, other: string, linked: Set<string>) {
  const names = [other, other.slice(0, 4)].filter(n => n.length >= 3).map(n => n.toLowerCase());
  // Ni las cuentas apartadas (allí pasan su nómina y lo que se lleva, no saldos)
  const reserved = new Set(c.wb.accounts.filter(a => accountUse(a).use === 'reserved').map(a => a.name));
  return c.movements.filter(m => m.account !== TRICOUNT_ACCOUNT && !reserved.has(m.account) && Math.abs(m.amount) >= 50 && !linked.has(m.id)
    && names.some(n => m.concept.toLowerCase().includes(n)) && m.effCategory.toLowerCase() !== SETTLE_CATEGORY.toLowerCase()
    && !/nomina|nómina/i.test(m.concept))
    .sort((a, b) => b.date.localeCompare(a.date));
}
